import { useMemo, useState } from "react";
import { Search } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { BucketBadge } from "@/components/settings/training-teen-bucket-badge";
import { TrainingContentModule } from "@/lib/api/training-simulator-content";
import { cn, getInitials } from "@/lib/utils";

interface TrainingTeenSidebarProps {
  modules: TrainingContentModule[];
  teenCount: number;
  selectedTeenId: string;
  onSelectTeen: (teenId: string) => void;
}

export function TrainingTeenSidebar({
  modules,
  teenCount,
  selectedTeenId,
  onSelectTeen,
}: TrainingTeenSidebarProps) {
  const [search, setSearch] = useState("");

  const filteredModules = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return modules;

    return modules
      .map((module) => ({
        ...module,
        levels: module.levels
          .map((level) => ({
            ...level,
            teens: level.teens.filter((teen) =>
              teen.display_name.toLowerCase().includes(query),
            ),
          }))
          .filter((level) => level.teens.length > 0),
      }))
      .filter((module) => module.levels.length > 0);
  }, [modules, search]);

  return (
    <Card className="lg:sticky lg:top-4 lg:flex lg:max-h-[calc(100vh-2rem)] lg:flex-col">
      <CardHeader className="space-y-3 p-4 pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base">Training Teens</CardTitle>
          <span className="text-xs text-gray-500">
            {teenCount} {teenCount === 1 ? "profile" : "profiles"}
          </span>
        </div>
        <div className="relative">
          <Search className="absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
          <Input
            className="h-9 pl-8"
            placeholder="Search teens..."
            value={search}
            onChange={(event) => setSearch(event.target.value)}
          />
        </div>
      </CardHeader>
      <CardContent className="overflow-y-auto p-2 pt-0">
        {filteredModules.length === 0 && (
          <p className="px-2 py-6 text-center text-sm text-gray-500">
            No teens match &ldquo;{search.trim()}&rdquo;
          </p>
        )}
        {filteredModules.map((module) => (
          <div key={module.id} className="mb-4 last:mb-0">
            <p className="px-2 pt-2 text-xs font-semibold uppercase tracking-wide text-gray-400">
              {module.position}. {module.title}
            </p>
            {module.levels.map((level) => (
              <div key={level.id} className="mt-2">
                <p className="px-2 text-sm font-medium text-gray-700">
                  {level.position}. {level.title}
                </p>
                <div className="ml-2 mt-1 space-y-0.5 border-l border-gray-100 pl-2">
                  {level.teens.map((teen) => {
                    const isSelected = teen.id === selectedTeenId;
                    return (
                      <button
                        key={teen.id}
                        type="button"
                        className={cn(
                          "flex w-full items-center gap-2.5 rounded-md px-2 py-1.5 text-left text-sm transition-colors hover:bg-gray-50",
                          isSelected && "bg-blue-50 hover:bg-blue-50",
                        )}
                        onClick={() => onSelectTeen(teen.id)}
                      >
                        <span
                          className={cn(
                            "flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-[11px] font-semibold uppercase",
                            isSelected
                              ? "bg-blue-600 text-white"
                              : "bg-gray-100 text-gray-600",
                          )}
                        >
                          {getInitials(teen.display_name) || "?"}
                        </span>
                        <span className="min-w-0">
                          <span
                            className={cn(
                              "block truncate",
                              isSelected && "font-medium text-blue-900",
                            )}
                          >
                            {teen.display_name}
                          </span>
                          <BucketBadge
                            bucket={teen.conversation_bucket}
                            className="mt-0.5 px-1.5 py-px text-[10px]"
                          />
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        ))}
      </CardContent>
    </Card>
  );
}
